import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import NavbarScroll from './NavbarScroll.js';
import { track } from '../../utils/analytics';
import { FaTimes } from 'react-icons/fa';
import {
  HiMenu,
  HiChevronDown,
  HiCode,
  HiChip,
  HiSparkles,
  HiDatabase,
  HiCog,
  HiOutlineHeart,
} from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { solutions } from '../../constants/solutions';

/* ────────────────────────────────────
   Services shown in the mega dropdown.
   Each renders as its own card so the
   full offering is visible at a glance
   instead of hidden behind hover states.
   ──────────────────────────────────── */
const serviceLinks = [
  {
    name: 'Web & Mobile Development',
    path: '/services/web-mobile-development',
    icon: HiCode,
    desc: 'Websites and apps built to convert',
  },
  {
    name: 'AI & Automation',
    path: '/services/ai-automation',
    icon: HiChip,
    desc: 'Intelligent workflows and automation',
  },
  {
    name: 'Generative Engine Optimization',
    path: '/services/generative-engine-optimization',
    icon: HiSparkles,
    desc: 'Get cited by ChatGPT and AI search',
  },
  {
    name: 'Custom Operations Software',
    path: '/services/custom-operations-software',
    icon: HiDatabase,
    desc: 'Internal systems for your workflows',
  },
  {
    name: 'Medical Software Development',
    path: '/services/medical-software-development',
    icon: HiOutlineHeart,
    desc: 'Clinical platforms for UAE providers',
    label: 'Custom Operations Software',
  },
  {
    name: 'Embedded Systems',
    path: '/services/embedded-systems',
    icon: HiCog,
    desc: 'Firmware, IoT and real-time systems',
  },
];

/* Specialist pages (see constants/solutions.js), tagged with their audience
   or deadline where Services cards show their parent discipline. */
const solutionLinks = solutions.map(({ name, path, icon, desc, tag }) => ({ name, path, icon, desc, label: tag }));

const menuItems = [
  { name: 'Home', path: '/' },
  // Points at the real /services hub rather than the homepage #Services
  // anchor it used before that page existed. This is the sitewide nav link
  // the hub needs - a page reachable only from the sitemap is exactly what
  // ends up "Discovered - currently not indexed".
  {
    name: 'Services',
    path: '/services',
    submenu: serviceLinks,
    heading: 'What we build',
    more: { label: 'See our work →', path: '/projects' },
  },
  // No hub page of its own, so the top-level item only opens the dropdown.
  {
    name: 'Solutions',
    submenu: solutionLinks,
    heading: 'Specialist solutions',
    more: { label: 'All services →', path: '/services' },
  },
  { name: 'Projects', path: '/projects' },
  // The blog was previously only reachable from the homepage, which left six
  // articles a long way from any entry point. A nav link puts every post one
  // hop from every page.
  { name: 'Insights', path: '/blogs' },
  { name: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileExpandedItems, setMobileExpandedItems] = useState({});
  const navscroll = NavbarScroll();
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);

  const closeMenu = () => {
    setIsOpen(false);
    setActiveDropdown(null);
    setMobileExpandedItems({});
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(navscroll.y > 100 && navscroll.y - navscroll.lastY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navscroll.y, navscroll.lastY]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleNavigation = () => closeMenu();

  // Dropdown hover handlers with a short delay to prevent flickering
  const handleDropdownEnter = useCallback((itemName) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(itemName);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    dropdownTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  }, []);

  const toggleMobileExpand = (itemName) => {
    setMobileExpandedItems((prev) => ({ ...prev, [itemName]: !prev[itemName] }));
  };

  // Navigation links that work both on the home page and on other routes
  const NavLink = ({ to, children, onClick, className = '', ...props }) => {
    if (to.startsWith('/')) {
      return (
        <RouterLink to={to} onClick={onClick} className={className} {...props}>
          {children}
        </RouterLink>
      );
    }

    if (location.pathname === '/') {
      return (
        <ScrollLink to={to} smooth duration={500} onClick={onClick} className={className} {...props}>
          {children}
        </ScrollLink>
      );
    }

    return (
      <RouterLink to={`/#${to}`} onClick={onClick} className={className} {...props}>
        {children}
      </RouterLink>
    );
  };

  /* ── One service, rendered as a card ── */
  const ServiceCard = ({ service, onClick }) => {
    const Icon = service.icon;
    const isActive = location.pathname === service.path;
    return (
      <NavLink
        to={service.path}
        onClick={onClick}
        className={`group/card flex items-start gap-3.5 rounded-2xl border p-4 transition-all duration-200 cursor-pointer ${isActive
          ? 'border-accent/40 bg-accent/10'
          : 'border-white/[0.07] bg-white/[0.03] hover:border-accent/30 hover:bg-white/[0.07]'
          }`}
      >
        <span
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ${isActive
            ? 'bg-accent text-primary'
            : 'bg-accent/10 text-accent group-hover/card:bg-accent group-hover/card:text-primary'
            }`}
        >
          <Icon className="text-xl" />
        </span>
        <span className="min-w-0">
          {service.label && (
            <span className="block text-[10px] font-bold uppercase tracking-widest text-white/30 mb-0.5">
              {service.label}
            </span>
          )}
          <span className={`block text-sm font-bold leading-snug ${isActive ? 'text-accent' : 'text-white group-hover/card:text-accent'}`}>
            {service.name}
          </span>
          <span className="block text-xs text-white/45 leading-snug mt-1">{service.desc}</span>
        </span>
      </NavLink>
    );
  };

  return (
    <>
      <div className={`${location.pathname === '/projects' ? 'sticky' : 'fixed'} top-0 left-0 right-0 z-50 transition-transform duration-300 ${scrolled && location.pathname !== '/projects' ? '-translate-y-full' : 'translate-y-0'
        }`}>
        <div className="absolute inset-0 bg-primary/95 backdrop-blur-lg border-b border-accent/20" />

        <nav className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Main">
          <div className="flex h-16 items-center justify-between relative">
            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden absolute left-0 inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-accent/20 hover:text-accent active:scale-95 transition"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <FaTimes className="h-6 w-6" /> : <HiMenu className="h-6 w-6" />}
            </button>

            {/* Logo */}
            <div className="flex-shrink-0 flex items-center mx-auto lg:ml-0 lg:mr-4 xl:mr-10 transition-transform hover:scale-105">
              <NavLink to="Header" className="flex items-center cursor-pointer relative group" onClick={closeMenu}>
                <img
                  className="h-12 w-auto md:h-14 lg:h-16"
                  src="/assets/logowdf-orig-removebg.webp"
                  alt="WDF - custom software, AI and web development"
                  width="150"
                  height="64"
                  loading="eager"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-theme-500/10 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </NavLink>
            </div>

            {/* Desktop Navigation */}
            {/* Tighter gaps at lg: six items plus the CTA only just fit at 1024px. */}
            <div className="hidden lg:flex lg:gap-x-1 xl:gap-x-6 items-center">
              {menuItems.map((item) =>
                item.submenu ? (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => handleDropdownEnter(item.name)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    {item.path ? (
                      <NavLink
                        to={item.path}
                        className="flex items-center gap-1 px-3 py-2 text-lg font-semibold text-white hover:text-accent transition-colors cursor-pointer relative group"
                        onClick={handleNavigation}
                        aria-expanded={activeDropdown === item.name}
                      >
                        {item.name}
                        <HiChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`}
                        />
                        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transform transition-transform origin-center" />
                      </NavLink>
                    ) : (
                      <button
                        type="button"
                        className="flex items-center gap-1 px-3 py-2 text-lg font-semibold text-white hover:text-accent transition-colors cursor-pointer relative group"
                        onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                        aria-expanded={activeDropdown === item.name}
                      >
                        {item.name}
                        <HiChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`}
                        />
                        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transform transition-transform origin-center" />
                      </button>
                    )}

                    {/* ── Full-width mega dropdown ── */}
                    <AnimatePresence>
                      {activeDropdown === item.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.2, ease: 'easeOut' }}
                          className="fixed left-0 right-0 top-16 z-50"
                          style={{ width: '100vw' }}
                          onMouseEnter={() => handleDropdownEnter(item.name)}
                          onMouseLeave={handleDropdownLeave}
                        >
                          <div className="bg-primary/[0.97] backdrop-blur-xl border-b border-accent/15 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7">
                              <div className="flex items-baseline justify-between mb-5">
                                <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">
                                  {item.heading}
                                </p>
                                <RouterLink
                                  to={item.more.path}
                                  onClick={closeMenu}
                                  className="text-sm font-semibold text-white/50 hover:text-accent transition-colors"
                                >
                                  {item.more.label}
                                </RouterLink>
                              </div>
                              {/* Up to three across; a shorter list fills the width
                                  rather than leaving empty columns. */}
                              <div className={`grid gap-3 ${item.submenu.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                                {item.submenu.map((service) => (
                                  <ServiceCard key={service.path} service={service} onClick={handleNavigation} />
                                ))}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    className="group px-3 py-2 text-lg font-semibold text-white hover:text-accent transition-colors relative cursor-pointer"
                    onClick={handleNavigation}
                  >
                    {item.name}
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transform transition-transform origin-center" />
                  </NavLink>
                )
              )}
            </div>

            {/* CTA Button + Language Switcher */}
            <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-4">
              <RouterLink
                to="/contact"
                onClick={() => { track('cta_click', { cta_label: "Let's Talk", cta_section: 'navbar', cta_type: 'nav_cta' }); closeMenu(); }}
                className="relative group inline-flex items-center gap-x-1.5 overflow-hidden whitespace-nowrap rounded-lg bg-accent px-5 py-3 text-[0.95rem] font-bold uppercase tracking-wide text-[#001810] shadow-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                <span className="relative z-10">Let's Talk</span>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
                </div>
                {/* CSS pulse - a JS-driven animation here would be snapshotted
                    mid-frame by the prerenderer and break hydration. */}
                <span className="wdf-cta-pulse absolute -z-10 inset-0 bg-accent/40" aria-hidden="true" />
              </RouterLink>
              <LanguageSwitcher />
            </div>
          </div>
        </nav>
      </div>

      {/* ── Mobile Navigation Drawer ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '-100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '-100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="lg:hidden fixed inset-0 z-40 bg-primary"
          >
            <div className="h-full overflow-y-auto px-5 py-6 border-r border-accent/20">
              <div className="flex items-center justify-between">
                <NavLink to="Header" onClick={closeMenu}>
                  <img
                    className="h-10 w-auto brightness-0 invert"
                    src="/assets/logowdf-orig-removebg.webp"
                    alt="WDF"
                    width="100"
                    height="40"
                    loading="eager"
                    decoding="async"
                  />
                </NavLink>
                <button
                  type="button"
                  className="-m-2.5 rounded-md p-2.5 text-white"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close menu"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-8 divide-y divide-white/10">
                <div className="space-y-1 pb-6">
                  {menuItems.map((item) => (
                    <div key={item.name} className="flex flex-col">
                      {item.submenu ? (
                        <button
                          type="button"
                          onClick={() => toggleMobileExpand(item.name)}
                          aria-expanded={!!mobileExpandedItems[item.name]}
                          className="-mx-3 flex items-center justify-between rounded-lg px-3 py-3 text-2xl font-bold leading-7 text-white hover:bg-accent/10 hover:text-accent transition-all duration-300"
                        >
                          {item.name}
                          <HiChevronDown
                            className={`h-5 w-5 transition-transform duration-200 ${mobileExpandedItems[item.name] ? 'rotate-180' : ''}`}
                          />
                        </button>
                      ) : (
                        <NavLink
                          to={item.path}
                          className="-mx-3 block rounded-lg px-3 py-3 text-2xl font-bold leading-7 text-white hover:bg-accent/10 hover:text-accent transition-all duration-300"
                          onClick={handleNavigation}
                        >
                          {item.name}
                        </NavLink>
                      )}

                      {item.submenu && (
                        <AnimatePresence>
                          {mobileExpandedItems[item.name] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-2 mb-3 flex flex-col gap-2.5">
                                {item.submenu.map((service) => (
                                  <ServiceCard key={service.path} service={service} onClick={handleNavigation} />
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </div>
                  ))}
                </div>

                <div className="py-3">
                  <LanguageSwitcher isMobile />
                </div>

                <div className="py-6">
                  <RouterLink
                    to="/contact"
                    className="-mx-3 block w-full rounded-lg bg-accent px-3 py-3.5 text-center text-sm font-bold uppercase leading-6 tracking-wide text-[#001810] shadow-sm"
                    onClick={() => { track('cta_click', { cta_label: "Let's Talk", cta_section: 'mobile_menu', cta_type: 'nav_cta' }); closeMenu(); }}
                  >
                    Let's Talk
                  </RouterLink>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
